import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "../../../hooks/usedebounce";
import { RuleSetList } from "../../../models/ruleset";
import { State } from "../../../models/state";
import { RuleSetService } from "../../../services/rule-set-services";
import { StateService } from "../../../services/state-services";

export type RulesetsState = {
  loading: boolean;
  rulesets: RuleSetList[];
  query: string;
  filterByActive: boolean | null
  debouncedQuery: string;
  page: number;
  pageSize: number;
  total: number;
  states: State[];
  stateId: number;
  showImport: boolean;
  importing: boolean;
  importComplete: boolean;
  importFile: File | undefined;
  error: string;
  setQuery: React.Dispatch<React.SetStateAction<string>>;
  startNewRuleSet: () => void;
  updatePaging: (page: number, pageSize: number) => void;
  updateState: (newStateId: number) => void;
  getExportLink: () => string;
  startImport: () => void;
  cancelImport: () => void;
  setFile: (file: File | undefined) => void;
  setErrorMessage: (message: string) => void;
  setFilterByActive: (value: boolean | null) => void
  clearErrorMessage: () => void;
  doImport: () => Promise<void>;
};

export default function useRulesets() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [rulesets, setRuleSets] = useState<RuleSetList[]>([]);
  const [pageSize, setPageSize] = useState<number>(15);
  const [page, setPage] = useState<number>(0);
  const [total, setTotal] = useState<number>(15);
  const [states, setStates] = useState<State[]>([]);
  const [stateId, setStateId] = useState<number>(-1);
  const [query, setQuery] = useState<string>("");
  const [filterByActive, setFilterByActive] = useState<boolean | null>(null)
  const [showImport, setShowImport] = useState<boolean>(false);
  const [importing, setImporting] = useState<boolean>(false);
  const [importComplete, setImportComplete] = useState<boolean>(false);
  const [importFile, setImportFile] = useState<File | undefined>();
  const [error, setError] = useState<string>("");
  const debouncedQuery = useDebounce(query, 800);

  const updateState = (newStateId: number) => {
    setStateId(newStateId);
    setPage(0);
    getRuleSets(0, pageSize, newStateId);
  };

  const getStates = useCallback(async () => {
    try {
      setLoading(true);
      const newStates = await StateService.getStates();
      setStates((old) => [
        { id: -1, name: "ALL STATES", abbv: "ALL" },
        ...newStates,
      ]);
    } catch (e) {
      //TODO: error display
      alert(e);
    } finally {
      setLoading(false);
    }
  }, [setStates]);

  const getRuleSets = useCallback(
    async (page: number, pageSize: number, stateId: number) => {
      if (debouncedQuery) {
        setLoading(true);
      }
      try {
        const newRuleSetsPaged = await RuleSetService.getRuleSetsPaged(
          page,
          pageSize,
          debouncedQuery,
          stateId
        );
        setPage(newRuleSetsPaged.page);
        setPageSize(newRuleSetsPaged.pageSize);
        setTotal(newRuleSetsPaged.total);
        setRuleSets(newRuleSetsPaged.rulesets);
      } catch (e) {
        //TODO: error display
        alert(e);
      } finally {
        setLoading(false);
      }
    },
    [debouncedQuery]
  );

  const updatePaging = (newPage: number, newPageSize: number) => {
    setPage(newPage);
    setPageSize(newPageSize);
    getRuleSets(newPage, newPageSize, stateId);
  };

  const getExportLink = () => {
    return `${RuleSetService.getBaseUrl()}/export`;
  };

  const startImport = () => {
    setShowImport(true);
  };

  const cancelImport = () => {
    setImportFile(undefined);
    setError("");
    setImportComplete(false);
    setImporting(false);
    setShowImport(false);
  };

  const setFile = (file: File | undefined) => {
    setImportFile(file);
  };

  const setErrorMessage = (message: string) => {
    setError(message);
  };

  const clearErrorMessage = () => {
    setError("");
  };

  const doImport = async () => {
    try {
      setErrorMessage("");
      setImporting(true);
      await RuleSetService.importData(importFile!);
      setImportComplete(true);
    } catch (e: any) {
      console.log(e);
      setErrorMessage(e.message ?? e);
    } finally {
      setImporting(false);
    }
  };

  useEffect(() => {
    setPage(0); // query changed go to page 1
    getRuleSets(0, pageSize, stateId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery]);

  useEffect(() => {
    getStates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (importComplete) {
      setTimeout(() => {
        cancelImport();
        getRuleSets(page, pageSize, stateId);
      }, 500);
    }
  }, [importComplete, getRuleSets, page, pageSize, stateId]);

  const startNewRuleSet = () => {
    navigate("/rulesets/create");
  };

  return {
    loading,
    rulesets,
    query,
    filterByActive,
    debouncedQuery,
    page,
    pageSize,
    total,
    states,
    stateId,
    showImport,
    importing,
    importComplete,
    importFile,
    error,
    setQuery,
    startNewRuleSet,
    updatePaging,
    updateState,
    getExportLink,
    startImport,
    cancelImport,
    setFile,
    setErrorMessage,
    setFilterByActive,
    clearErrorMessage,
    doImport,
  };
}
